/**
 * Spotlight — wrapper que dibuja un radial gradient siguiendo el cursor
 * dentro del propio elemento. Útil para cards "vivas".
 *
 * Implementación: actualiza CSS custom props vía mousemove. Cero re-renders.
 * El gradiente real lo pinta `.spotlight::before` definido en `globals.css`.
 *
 * Uso:
 *   <Spotlight className="rounded-2xl bg-white p-8">...</Spotlight>
 */
import { useRef, type CSSProperties, type MouseEvent, type ReactNode } from 'react';

import { useCoarsePointer } from '../../hooks/useMediaQuery';
import { cn } from '../../utils/cn';

export interface SpotlightProps {
  children: ReactNode;
  className?: string;
  /** Color del haz (rgba o hex). */
  color?: string;
  /** Radio del haz en px. */
  size?: number;
}

export function Spotlight({
  children,
  className,
  color = 'rgba(255, 200, 54, 0.18)',
  size = 480,
}: SpotlightProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isTouch = useCoarsePointer();

  const handleMove = (e: MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--spot-x', `${e.clientX - rect.left}px`);
    el.style.setProperty('--spot-y', `${e.clientY - rect.top}px`);
  };

  // En touch no hay hover real → renderizamos sin spotlight.
  if (isTouch) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      className={cn('spotlight', className)}
      style={{ '--spot-color': color, '--spot-size': `${size}px` } as CSSProperties}
    >
      {children}
    </div>
  );
}
